// The athlete's strategy as the stream builds it (spec §13.3, §13.5; D49):
//   - `strategy`: the committed one's phase timeline and outline weeks, from
//     GET /v2/strategy (null: the athlete has none yet);
//   - `draft`: the draft a creation run is writing, app-only until Accept:
//     its phases (`strategy.phase`, provisional first, then final, upserted
//     by phase id), outline weeks (`week.outline`), written sessions
//     (`draft.session.upsert` / `.remove`, keyed until Accept gives them ids)
//     and stage (`draft.status`). One open draft per athlete: an event for
//     another draft id means a new run abandoned the earlier one;
//   - `work`: what each recent run showed while it worked (`run.status`
//     steps, `coach.insight` lines), for the thread's working item;
//   - `goal`: the athlete's one non-race goal (`goal.upsert`).
//
// M6: the committed strategy also carries `on_track`, the race screen's
// indicator per goal (GET /v2/strategy's latest trajectory reading per
// goal_key); no event carries it, so the client re-reads the strategy when
// the coach changes the plan or says how the athlete is tracking.
//
// Plain data, never mutated, like reducer.js (which calls in here).

export const MAX_RUNS = 6;           // runs whose working state is kept
export const DONE_STAGES = new Set(['proposed', 'accepted', 'dismissed']);

const DAY_MS = 86400000;
const str = (v) => (v == null ? null : String(v));

/** A TrainingPhase (a `strategy.phase` event, a strategy document) as a timeline phase. */
export function toTimelinePhase(p) {
  const blockWeeks = (p.blocks || []).reduce((n, b) => n + (b.weeks || []).length, 0);
  const days = Math.round((Date.parse(p.end_date) - Date.parse(p.start_date)) / DAY_MS) + 1;
  return {
    id: String(p.id), name: p.name, kind: p.kind ?? null, start_date: p.start_date, end_date: p.end_date,
    weeks: p.weeks ?? (blockWeeks || Math.floor(days / 7)), objective: p.objective || '',
  };
}

/** A strategy document's outline weeks, by week id, each with its phase and block. */
export function documentWeeks(doc) {
  const out = {};
  for (const ph of doc?.phases || []) {
    for (const b of ph.blocks || []) {
      for (const w of b.weeks || []) out[String(w.id)] = { ...w, phase_id: String(ph.id), block_id: String(b.id) };
    }
  }
  return out;
}

// `since_seq`: the seq the build started at, when this client saw it start
// (null for a draft first known from a read): a failure message after it ends it.
const emptyDraft = (id, runId = null, seq = null) => ({
  id: String(id), run_id: str(runId), since_seq: seq, stage: 'started', proposal_item_id: null, of: 0, ofFinal: false, phases: {}, weeks: {}, sessions: {},
});

/** The draft an event is about: the open one, or a fresh one (a new run abandons the earlier draft). */
function draftFor(state, id, runId, seq = null) {
  const d = state.draft;
  if (d && d.id === String(id)) return d.run_id || !runId ? d : { ...d, run_id: String(runId) };
  return emptyDraft(id, runId, seq);
}

/**
 * The draft's phases, in order. Provisional phases are laid end to end as the
 * coach chooses them; once code has fitted them to the race each comes again,
 * final, with the same id. When every final phase is in, a provisional one
 * that never came back final is dropped.
 */
export function draftTimeline(draft) {
  if (!draft) return [];
  const phases = Object.values(draft.phases || {});
  const final = phases.filter((p) => !p.provisional);
  const settled = draft.ofFinal && final.length >= draft.of;
  return (settled ? final : phases).sort((a, b) => (a.index - b.index) || String(a.start_date).localeCompare(String(b.start_date)));
}

/**
 * The strategy slice from the reads: GET /v2/strategy (`read`: its body,
 * null on 404, undefined when it couldn't be read) and /v2/calendar's
 * `draft` (the calendar is the authority on whether a draft is open). What
 * a read doesn't carry is kept from `prev`.
 */
export function strategyFromReads(read, calDraft, prev = {}) {
  const strategy = read === undefined ? prev.strategy ?? null
    : read && { strategy_id: read.strategy_id, version: read.version, timeline: read.timeline || [],
      weeks: documentWeeks(read.document), goals: read.document?.goals || [],
      on_track: Array.isArray(read.on_track) ? read.on_track : prev.strategy?.on_track || [] };
  const id = calDraft?.draft_strategy_id ?? (calDraft === undefined ? read?.draft?.draft_strategy_id : null);
  if (!id) return { strategy, draft: null };
  const before = prev.draft?.id === String(id) ? prev.draft : emptyDraft(id);
  const rd = read?.draft?.draft_strategy_id === id ? read.draft : null;
  const phases = rd?.timeline
    ? Object.fromEntries(rd.timeline.map((p, index) => [String(p.id), { ...toTimelinePhase(p), index, provisional: false }]))
    : before.phases;
  const draft = {
    ...before,
    stage: rd?.stage || before.stage,
    proposal_item_id: calDraft?.proposal_item_id ?? before.proposal_item_id,
    of: rd?.timeline ? rd.timeline.length : before.of,
    ofFinal: rd?.timeline ? true : before.ofFinal,
    phases,
    weeks: rd?.document ? documentWeeks(rd.document) : before.weeks,
    sessions: calDraft ? Object.fromEntries((calDraft.sessions || []).map((s) => [String(s.key), s.workout])) : before.sessions,
  };
  return { strategy, draft };
}

function withRun(work, runId, seq, fn) {
  if (!runId) return work;
  const k = String(runId);
  const run = work[k] || { run_id: k, seq, steps: [], insights: [], state: 'running' };
  const next = { ...work, [k]: fn(run) };
  const keys = Object.keys(next);
  if (keys.length <= MAX_RUNS) return next;
  const oldest = keys.sort((a, b) => next[a].seq - next[b].seq).slice(0, keys.length - MAX_RUNS);
  oldest.forEach((o) => delete next[o]);
  return next;
}

/** One strategy, goal or working-state event onto the state; anything else returns `s` itself. */
export function applyStrategyEvent(s, event) {
  const p = event.payload || {};
  const runId = event.run_id ?? null;
  switch (event.type) {
    case 'strategy.phase': {
      const d = draftFor(s, p.draft_strategy_id, runId, event.seq);
      const ph = { ...toTimelinePhase(p.phase), index: p.index, provisional: Boolean(p.provisional) };
      // While provisional, `of` is the phases so far (a lower bound); a final phase carries the count.
      const of = p.provisional ? (d.ofFinal ? d.of : Math.max(d.of, p.of)) : p.of;
      return { ...s, draft: { ...d, of, ofFinal: d.ofFinal || !p.provisional, phases: { ...d.phases, [ph.id]: ph } } };
    }
    case 'week.outline': {
      const d = draftFor(s, p.draft_strategy_id, runId, event.seq);
      const w = { ...p.week, phase_id: String(p.phase_id), block_id: String(p.block_id) };
      return { ...s, draft: { ...d, weeks: { ...d.weeks, [String(w.id)]: w } } };
    }
    case 'draft.session.upsert': {
      const d = draftFor(s, p.draft_strategy_id, runId, event.seq);
      return { ...s, draft: { ...d, sessions: { ...d.sessions, [String(p.key)]: p.workout } } };
    }
    case 'draft.session.remove': {
      if (!s.draft || s.draft.id !== String(p.draft_strategy_id) || !(String(p.key) in s.draft.sessions)) return s;
      const sessions = { ...s.draft.sessions };
      delete sessions[String(p.key)];
      return { ...s, draft: { ...s.draft, sessions } };
    }
    case 'draft.status': {
      const work = withRun(s.work || {}, runId, event.seq, (r) => ({ ...r, draft_strategy_id: String(p.draft_strategy_id),
        stage: p.stage, ...(DONE_STAGES.has(p.stage) ? { state: 'finished' } : {}) }));
      const mine = s.draft && s.draft.id === String(p.draft_strategy_id);
      if (p.stage === 'accepted') {
        // The draft is now the plan: its phases are the strategy's until the
        // client re-reads /v2/strategy; its sessions arrive as session.upsert.
        if (!mine) return { ...s, work };
        return { ...s, work, draft: null,
          strategy: { ...(s.strategy || {}), timeline: draftTimeline(s.draft).map(({ index, provisional, ...ph }) => ph),
            weeks: s.draft.weeks, stale: true } };
      }
      if (p.stage === 'dismissed') return { ...s, work, draft: mine ? null : s.draft };
      const d = p.stage === 'started' && !mine ? emptyDraft(p.draft_strategy_id, runId, event.seq) : draftFor(s, p.draft_strategy_id, runId, event.seq);
      // A build (re)starting on this draft: only failures after this point end it.
      const since = p.stage === 'started' ? event.seq : d.since_seq;
      // ...and an earlier build's proposal is not this one's (draftProposed).
      const proposal = p.proposal_item_id ?? (p.stage === 'started' ? null : d.proposal_item_id);
      return { ...s, work, draft: { ...d, since_seq: since ?? null, stage: p.stage, proposal_item_id: proposal } };
    }
    case 'coach.insight': {
      const run = runId ?? s.draft?.run_id;
      return { ...s, work: withRun(s.work || {}, run, event.seq, (r) => ({ ...r,
        insights: [...r.insights, { text: p.text, stage: p.stage, evidence_refs: p.evidence_refs || [] }] })) };
    }
    case 'run.status': {
      const run = p.run_id ?? runId;
      return { ...s, work: withRun(s.work || {}, run, event.seq, (r) => {
        if (p.state && p.state !== 'started') return { ...r, state: p.state };
        const steps = r.steps.filter((x) => x.stage !== p.stage);
        return { ...r, state: 'running', steps: p.label ? [...steps, { stage: p.stage, label: p.label }] : steps };
      }) };
    }
    case 'goal.upsert':
      return { ...s, goal: p, goal_seq: event.seq };
    default:
      return s;
  }
}

/** The thread item a draft's sessions are a proposal for, or null. */
export const draftProposalId = (draft) => draft?.proposal_item_id ?? null;

/** The open draft's written sessions, by date. */
export function draftSessions(draft) {
  return Object.entries(draft?.sessions || {})
    .map(([key, w]) => ({ key, ...w }))
    .sort((a, b) => String(a.scheduled_date).localeCompare(String(b.scheduled_date)) || a.key.localeCompare(b.key));
}

// The coach's messages that end a build without a proposal (backend repair.py).
export const BUILD_ENDED_KINDS = new Set(['plan_failed', 'plan_not_applied']);

/**
 * The coach message saying the open draft's build ended without a proposal
 * (aithlo-frontend#66), or null. The backend leaves the draft open then, so
 * this is what ends the working item: a plan_failed / plan_not_applied
 * coach_message for this draft's run, or after the build started (any one
 * when the start isn't known: the draft was first seen in a read), and not
 * one the athlete has since asked to rebuild from.
 */
export function draftFailure(state, { outcomes = {}, pending = [] } = {}) {
  const d = state?.draft;
  if (!d || DONE_STAGES.has(d.stage)) return null;
  const rebuilding = (item) => outcomes?.[String(item.id)]?.status === 'rebuilding'
    || pending.some((c) => c.op?.kind === 'rebuild' && String(c.op.id) === String(item.id));
  const items = Object.values(state.thread || {});
  // Start unknown: only a failure nothing from the coach has come after.
  const newestCoach = Math.max(-Infinity, ...items.filter((x) => x.type === 'coach_message' || x.type === 'proposal').map((x) => x.seq));
  const forThisBuild = (x) => {
    if (d.run_id && x.refs?.agent_run_id) return String(x.refs.agent_run_id) === d.run_id;
    return d.since_seq != null ? x.seq > d.since_seq : x.seq === newestCoach;
  };
  const ended = items.filter((x) => x.type === 'coach_message' && BUILD_ENDED_KINDS.has(x.body?.kind) && forThisBuild(x));
  const last = ended.sort((a, b) => b.seq - a.seq)[0] || null;
  return last && !rebuilding(last) ? last : null;
}

/**
 * Whether the draft's proposal is already in hand: its proposal item named
 * (`proposal_item_id`), or a strategy proposal from the draft's run in the
 * thread. Either is the end of the build, even before its draft.status
 * proposed arrives (29 Oct review: "working" should end on the proposal).
 */
export function draftProposed(state) {
  const d = state?.draft;
  if (!d) return false;
  if (d.proposal_item_id != null) return true;
  return Boolean(d.run_id) && Object.values(state.thread || {}).some((x) => x.type === 'proposal'
    && x.body?.kind === 'strategy' && x.refs?.agent_run_id != null && String(x.refs.agent_run_id) === d.run_id
    && (d.since_seq == null || x.seq > d.since_seq));
}

/**
 * The creation in progress, for the thread's working item: null when no
 * draft is being written. `done` once the draft is proposed (or its proposal
 * is in the thread), accepted or dismissed, or its build failed (`failed`:
 * the coach's message about it).
 */
export function creationView(state, { outcomes = {}, pending = [] } = {}) {
  const d = state?.draft;
  if (!d) return null;
  const work = (d.run_id && state.work?.[d.run_id]) || { steps: [], insights: [] };
  const failed = draftFailure(state, { outcomes, pending });
  return {
    draft: d, run_id: d.run_id, stage: d.stage, done: DONE_STAGES.has(d.stage) || draftProposed(state) || Boolean(failed), failed,
    steps: work.steps, insights: work.insights,
    phases: draftTimeline(d), sessions: Object.keys(d.sessions).length,
  };
}
